import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {css} from '@emotion/css';
import {FormProvider, useForm} from 'react-hook-form';

import RadioButton from 'patient-ping-remedy/packages/radio-button';
import Modal from "patient-ping-remedy/packages/modal";
import Button, {StyleType} from "patient-ping-remedy/packages/button";

import {useMutation} from "@tanstack/react-query";
import {ToastType} from "patient-ping-remedy/packages/toast";
import {useAlertStore} from "@/store/alert_store";
import {CarecoApiContext} from "@/app-context/careco-api-context";
import {GENERICERROR, SUCCESS_DISCHARGE} from "@manual_encounters/constants/BannerMessages";
import {ADMIT_PATIENT} from "@manual_encounters/CreateAdmission";
import {getDefaultValues} from "@manual_encounters/encounter_admit/pings/CreateEditClientModal";
import PatientCard from "@manual_encounters/encounter_reconcile/pings/patient/PatientCard";
import DischargeHookForm from "@manual_encounters/encounter_update_status/pings/components/DischargeHookForm";
import {StyledSameEncounter, StyledSameRadioButton, StyledSmallTypography}
  from "@manual_encounters/encounter_reconcile/pings/ReconcileHookModal.styles";
import Helpers from "@/helpers/helpers";
import {log} from "@/helpers/logger.js";
import {useMixpanel} from "@/hooks/useMixpanel.ts";
import {MixpanelEventType} from "@/api/dto/mixpanel.js";
import {useNavigate} from "react-router-dom";
import {useStateConfig} from "@/hooks/useStateConfig.js";
import {useAuthStore} from "@/store/auth_store.js";
import {useRosterStore} from "@/store/roster_store.js";
import PermissionsHelpers from "@/helpers/permissions_helpers.js";
import {FormSubmitType} from "@manual_encounters/constants/common";
import {SearchParam} from "@/helpers/search_params.js";
import {FormCodeBinding} from "@/api/dto/forms.js";
import {GroupTypes} from "@clients/profile_page/summary_tab/timeline_components/ADTCard.constants.js";
import {format} from "date-fns";
import {Feature, FeatureNew, FeaturePermissionLevel} from "@/api/dto/permissions.js";
import {FeatureToggle} from "@/api/dto/dto.js";
import {useFeatureToggles} from "@/hooks/useFeatureToggles";

const ReconcileHookModal = ({ patientDetail, hideModal, group, setStep }) => {
  const [patient, setPatient] = useState(patientDetail);
  const [isSameEncounter, setIsSameEncounter] = useState(true);
  const {mixpanelEventProperties} = useMixpanel();
  const navigate = useNavigate();
  const stateConfig = useStateConfig();
  const { currentRoster } = useRosterStore();
  const { currentUser } = useAuthStore();
  const {checkBooleanToggle} = useFeatureToggles();

  const isNewPermissionsEnabled = checkBooleanToggle(FeatureToggle.CARECO_3415_NEW_USER_PERMISSIONS);

  const { addAlert } = useAlertStore();
  const { carecoApi, encounterApi } = useContext(CarecoApiContext);

  const dischargePatient = useMutation({
    mutationFn: (payload) => encounterApi.createDischargeStatus(payload, {patientId: patient.publicPatientId}, group.id),
    onSuccess: () => {
      addAlert({content: SUCCESS_DISCHARGE, type: ToastType.SUCCESS});
      void carecoApi.postMixpanelEvent({
        eventName: MixpanelEventType.BIH_MANUAL_DISCHARGE_CREATED,
        ...mixpanelEventProperties
      });

      setStep(ADMIT_PATIENT);
    },
    onError: (error) => {
      addAlert({
        content: `Failed to discharge client. ${GENERICERROR} ${Helpers.traceId(error)}`,
        type: ToastType.ERROR
      });
      log('error', error);
    }
  });

  const dischargePatientWithCrf = useMutation({
    mutationFn: (payload) => encounterApi.createDischargeStatus(payload, {patientId: patient.publicPatientId}, group.id),
    onSuccess: (_data, variables) => {
      addAlert({content: SUCCESS_DISCHARGE, type: ToastType.SUCCESS});
      void carecoApi.postMixpanelEvent({
        eventName: MixpanelEventType.BIH_MANUAL_DISCHARGE_CREATED,
        ...mixpanelEventProperties
      });

      const eventDisplayName = (GroupTypes[group.type] ?? GroupTypes['Unknown']).displayName;
      const eventDate = variables.options.date;
      const params = new URLSearchParams({
        [SearchParam.PATIENT_ID]: patient.publicPatientId || patient.patientId,
        [SearchParam.ROSTER_ID]: String(group.id),
        [SearchParam.FORM_TYPE]: FormCodeBinding.DSAMH_CRF_DISCHARGE_FORM,
        [SearchParam.FORM_ENCOUNTER]: patient.currentPingEncounter.id,
        [SearchParam.FORM_EVENT_TYPE]: eventDisplayName,
        [SearchParam.FORM_EVENT_DATE]: format(new Date(eventDate ?? 0), 'yyyy-MM-dd'),
      });
      const url = `/clients/profile/forms?${params.toString()}`;
      hideModal();
      navigate(url);
    },
    onError: (error) => {
      addAlert({
        content: `Failed to discharge client. ${GENERICERROR} ${Helpers.traceId(error)}`,
        type: ToastType.ERROR
      });
      log('error', error);
    }
  });

  const calculateVisitDuration = useCallback((eventDate) => {
    const today = new Date();
    const admitDate = new Date(eventDate);
    return Helpers.calculateTimeDifference(admitDate, today);
  }, []);

  useEffect(() => {
    encounterApi?.getEncounters(patientDetail.publicPatientId, group.id).then((patientEncounter) => {
      if (patientEncounter) {
        const currentEncounter = patientEncounter.find(encounter => encounter.isOpen);

        if (currentEncounter) {
          setPatient(prevState => ({
            ...prevState,
            id: prevState.patientId,
            currentPing: {
              ...currentEncounter.events.at(-1),
              editable: group.admittingFacility && !group.automated,
              manual: !group.automated,
              state: currentEncounter.events.at(-1).status,
              facility: { ...group }
            },
            currentPingEncounter: {
              ...currentEncounter,
              visitDuration: calculateVisitDuration(currentEncounter.events.at(-1).eventDate),
              id: currentEncounter.encounterId
            },
          }));
        }
      }
    }).catch((error) => {
      log('error', error);
    });
  }, [carecoApi]);

  const defaultValues = useMemo(() => getDefaultValues(patient, group), [patient, group]);
  const hookFormMethods = useForm({ defaultValues, mode: 'onChange' });

  useEffect(() => {
    return () => hookFormMethods.unregister('reconcilePatient');
  }, [hookFormMethods]);

  const reconcilePatient = async ({
    dischargeDate,
    dispositionType,
    facility,
    secondaryFacility,
    locationDescription,
  }, submitType) => {
    if (isSameEncounter) {
      hideModal();
    } else {
      const options = {
        date: new Date(dischargeDate),
        disposition:{
          dispositionType: {
            id: dispositionType.id,
            itemCode: dispositionType.code
          },
          location: {
            facility: facility ? { facilityName: facility.label, id: facility.id } : null,
            secondaryFacility: secondaryFacility ? { facilityName: secondaryFacility.label, id: secondaryFacility.id } : null,
            locationDescription: locationDescription?.label || '',
          }
        },
      };

      const payload = { encounterId: patient.currentPingEncounter.id, options, submitType };
      if (submitType === FormSubmitType.WITH_CRF) {
        dischargePatientWithCrf.mutate(payload);
      } else {
        dischargePatient.mutate(payload);
      }
      hookFormMethods.reset({ ...defaultValues, date: new Date(), setting: '', admittedFrom: { label: '', id: '' } });
    }
  };

  const isDisabled = dischargePatient.isPending || dischargePatientWithCrf.isPending || !hookFormMethods.formState.isValid;

  const isDischargeWithCrfAvailable = !isSameEncounter && (
    (isNewPermissionsEnabled ?
      PermissionsHelpers.canForUserFeaturePermissionRosterNew(
        currentUser, currentRoster,
        FeaturePermissionLevel.ACT,
        FeatureNew.FORMS
      ) : PermissionsHelpers.canForUserFeaturePermissionRoster(
        currentUser, currentRoster,
        FeaturePermissionLevel.ACT,
        Feature.FORMS
      ))
    && stateConfig?.features?.crfForms
    && group?.configuration?.contractedProvider
  );

  const dischargeWithCrfButton = (
    <Button
      key="discharge+crf"
      styleType={StyleType.PRIMARY}
      disabled={isDisabled}
      onClick={hookFormMethods.handleSubmit((formData) => reconcilePatient(formData, FormSubmitType.WITH_CRF))}
    >
      Update + Discharge CRF
    </Button>
  );

  const modalButtons = [
    ...(isDischargeWithCrfAvailable ? [dischargeWithCrfButton] : []),
    <Button
      key="submit"
      styleType={isDischargeWithCrfAvailable ? StyleType.SECONDARY : StyleType.PRIMARY}
      disabled={isDisabled}
      onClick={hookFormMethods.handleSubmit((formData) => reconcilePatient(formData))}
    >
      {isSameEncounter ? 'Done' : 'Update to add new encounter'}
    </Button>,
    <Button key="cancel" styleType={StyleType.TERTIARY} onClick={hideModal}>
      Cancel
    </Button>,
  ];

  return (
    <Modal
      buttons={modalButtons}
      headerText="Reconcile Open Encounter"
      className={css({ width: '75rem' })}
    >
      <StyledSmallTypography>
        All fields marked with <span>*</span> are required.
      </StyledSmallTypography>
      <StyledSmallTypography>
        We currently have an active admission to your facility for the client you selected. Is this the same admission
        or a new admission?
      </StyledSmallTypography>
      <StyledSameEncounter>
        <StyledSameRadioButton
          label="Same"
          checked={isSameEncounter}
          onChange={() => setIsSameEncounter(true)}
        />
        <PatientCard patient={patient}/>
      </StyledSameEncounter>
      <RadioButton
        label="New"
        checked={!isSameEncounter}
        onChange={() => setIsSameEncounter(false)}
      />
      {!isSameEncounter &&
        <FormProvider {...hookFormMethods} >
          <DischargeHookForm patient={patient} isReconcile={true}/>
        </FormProvider>}
    </Modal>
  );
};

export default ReconcileHookModal;
